<script setup lang="ts">
import { date, dateTime, time, useSchedule } from '@/helpers';
import { computed, ref } from 'vue';

import type { Flight } from '@/types/flight';

import KL from '@/assets/KL.png?base64';
import FormatName from './FormatName.vue';

const props = defineProps<{ flight: Flight }>();

const showFlightOut = ref<boolean>(false);
const showFlightIn = ref<boolean>(false);

const { 
  outboundFlightTotalDurationMinutes,
  returnFlightTotalDurationMinutes,

  outboundFlightScheduleMessage,
  returnFlightScheduleMessage,
  
  outboundFlight,
  returnFlight,

  outboundFlightSchedule,
  returnFlightSchedule,

  surchargeMessage,
} = useSchedule(props.flight);


function calculateDayDifference(arrivalDateTime: string, departureDateTime: string): number {
    const arrivalDate = new Date(arrivalDateTime);
    const departureDate = new Date(departureDateTime);
    
    const timeDifference = arrivalDate.getTime() - departureDate.getTime();
    
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    
    return Math.ceil( dayDifference );
}

const dayDifference = computed(() => calculateDayDifference(props.flight.departure_date_time, props.flight.arrival_date_time));
const dayDifferenceReturn = computed(() => calculateDayDifference(props.flight.return_arrival_date_time, props.flight.return_departure_date_time));

</script>

<template>
  <div class="block margin">
    <h3 class="block__title">Vluchtinformatie</h3>
  </div>
  <div class="block block--flight">
    <div class="block__content">
      <h3 class="block__title">Heenvlucht</h3>
      <p class="block__date">{{ date.format(new Date(flight.departure_date_time)) }}</p>
      <a @click="showFlightOut = !showFlightOut" class="block__more" :class="{ open: showFlightOut }">Vluchtinformatie</a>
    </div>
    <div class="block__flight">
      <span class="origin">
        <p class="block__time">{{ time.format(new Date(flight.departure_date_time)) }}</p>
        <p class="block__text">
          <FormatName :name="flight.originName" :origin="flight.origin" />
        </p>
      </span>
      <span class="duration">
        <p class="block__label">{{ outboundFlightScheduleMessage }}</p>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 129.778 31.682">
          <path
            fill="#1A4C8D"
            d="m125.403 17.633.625.463a.834.834 0 0 1 .335.712l-.388 7.57 3.135 2.325a1.65 1.65 0 1 1-1.968 2.653l-3.135-2.325-7.132 2.566a.833.833 0 0 1-.778-.114l-.625-.464a.834.834 0 0 1 .084-1.393l5.229-2.985-3.224-2.392-1.766.415a.831.831 0 0 1-.687-.142l-1.028-.762 2.655-2.18 1.317-3.174 1.028.762a.834.834 0 0 1 .335.617l.115 1.81 3.223 2.392 1.341-5.87a.833.833 0 0 1 1.309-.484ZM1.471 23.149l1.357 1.47c-.484.447-.963.9-1.438 1.359L0 24.54c.486-.47.976-.933 1.471-1.39Zm3.03-2.672 1.289 1.53c-.503.423-1.002.853-1.497 1.29l-1.323-1.5a88.134 88.134 0 0 1 1.532-1.32Zm110.403-1.622c.528.418 1.051.844 1.57 1.275l-1.278 1.538a85.766 85.766 0 0 0-1.535-1.247l1.243-1.566ZM7.662 17.938l1.216 1.587c-.523.4-1.041.808-1.556 1.221l-1.252-1.56a90.956 90.956 0 0 1 1.592-1.248Zm104.008-1.53c.547.394 1.09.795 1.628 1.202l-1.207 1.595a85.306 85.306 0 0 0-1.59-1.174l1.169-1.623Zm-100.738-.857 1.142 1.642a84.18 84.18 0 0 0-1.61 1.146l-1.178-1.616a87.172 87.172 0 0 1 1.646-1.172Zm97.384-1.442c.565.368 1.126.743 1.682 1.125l-1.13 1.65a84.986 84.986 0 0 0-1.644-1.1l1.092-1.675Zm-93.994-.798 1.063 1.694c-.557.35-1.111.706-1.661 1.07l-1.102-1.669c.562-.372 1.13-.737 1.7-1.095Zm90.546-1.336c.582.341 1.16.69 1.733 1.045l-1.052 1.7a84.794 84.794 0 0 0-1.693-1.02l1.012-1.725Zm-87.083-.727.983 1.741c-.572.323-1.141.654-1.707.99l-1.023-1.718a86.752 86.752 0 0 1 1.747-1.013ZM101.317 10c.597.314 1.19.635 1.78.963l-.972 1.748a84.71 84.71 0 0 0-1.738-.94l.93-1.77Zm-79.955-.664.901 1.786c-.589.297-1.174.601-1.756.912l-.943-1.764a86.7 86.7 0 0 1 1.798-.934Zm76.32-1.14a86.71 86.71 0 0 1 1.824.88l-.889 1.79a84.71 84.71 0 0 0-1.781-.857l.847-1.812ZM25.03 7.593l.817 1.826a85.86 85.86 0 0 0-1.798.83L23.19 8.44c.61-.29 1.224-.573 1.84-.849Zm68.94-1.027a84.5 84.5 0 0 1 1.862.792l-.804 1.831a84.78 84.78 0 0 0-1.82-.774l.762-1.849Zm-65.196-.544.73 1.862c-.613.24-1.223.488-1.83.742l-.773-1.844c.621-.26 1.246-.514 1.873-.76Zm61.412-.912a86.9 86.9 0 0 1 1.896.704l-.718 1.867a84.9 84.9 0 0 0-1.852-.688l.674-1.883Zm-57.593-.483.642 1.894c-.624.211-1.245.43-1.863.656l-.686-1.879a86.953 86.953 0 0 1 1.907-.671Zm53.742-.795c.645.198 1.287.403 1.927.615l-.63 1.898a85.053 85.053 0 0 0-1.882-.6l.585-1.913Zm-49.922-.404.554 1.922a85.11 85.11 0 0 0-1.893.57l-.599-1.908a87.11 87.11 0 0 1 1.938-.584Zm46.013-.694c.653.167 1.304.342 1.953.524l-.54 1.926a85.221 85.221 0 0 0-1.91-.513l.497-1.937Zm-42.094-.339.465 1.946c-.64.153-1.28.313-1.916.481l-.51-1.934a87.28 87.28 0 0 1 1.961-.493ZM78.48 1.82c.66.137 1.318.282 1.975.434l-.45 1.948a85.388 85.388 0 0 0-1.931-.423l.406-1.959Zm-34.183-.275.374 1.965c-.647.123-1.293.254-1.938.392l-.42-1.955c.66-.142 1.322-.276 1.984-.402Zm30.197-.454c.667.106 1.332.22 1.996.341L76.13 3.4a85.541 85.541 0 0 0-1.95-.334l.314-1.975ZM48.292.88l.283 1.98c-.652.092-1.303.193-1.952.3l-.329-1.972c.665-.11 1.33-.214 1.998-.309ZM70.476.545c.67.075 1.34.158 2.007.249l-.268 1.982a85.669 85.669 0 0 0-1.962-.243l.223-1.988ZM52.328.4l.191 1.991a86.15 86.15 0 0 0-1.966.211L50.316.615c.67-.08 1.34-.152 2.012-.216ZM66.514.193A87.76 87.76 0 0 1 68.53.35l-.178 1.992a85.76 85.76 0 0 0-1.97-.154l.132-1.995ZM56.377.106l.098 1.997c-.658.033-1.317.073-1.974.12L54.356.23c.673-.049 1.347-.09 2.02-.123Zm6.101-.088c.674.013 1.347.035 2.02.064l-.086 1.998a85.813 85.813 0 0 0-1.975-.063l.041-2ZM60.436 0l.006 2c-.658.002-1.317.011-1.975.029l-.052-2A87.822 87.822 0 0 1 60.436 0Z"
          />
        </svg>
        <p class="block__label">{{ outboundFlightTotalDurationMinutes }}</p>
      </span>
      <span class="destination">
        <p class="block__time">
          {{ time.format(new Date(flight.arrival_date_time)) }} 
          <sup>{{ dayDifference > 0 ? `+${dayDifference}` : '' }}</sup>
        </p>
        <p class="block__text">
          <FormatName :name="flight.destinationName" :origin="flight.destination" />
        </p>
      </span>
    </div>
    <div class="block__footer">
      <template v-if="showFlightOut">
        <div class="block__content flex">
          <img :src="`data:image/png;base64,${KL}`" alt="klm" class="block__logo" />
        </div>
        <div class="block__meta">
          <template v-for="(schedule, index) in outboundFlight" :key="index">
            <span>
              <p class="block__label">Vertrek</p>
              <p class="block__text">{{ schedule.originName }} ({{ schedule.airport_origin }})</p>
              <p class="block__text">{{ dateTime.format(new Date(schedule.departure_date_time)) }}</p>
            </span>
            <span>
              <p class="block__label">Aankomst</p>
              <p class="block__text">{{ schedule.destinationName }} ({{ schedule.airport_destination }})</p>
              <p class="block__text">{{ dateTime.format(new Date(schedule.arrival_date_time)) }}</p>
            </span>
            <span>
              <p class="block__label">Vluchtnummer</p>
              <p class="block__text">KLM - Delta ({{ schedule.flight_code }})</p>
            </span>
            <span>
              <p class="block__label">Tijdsduur</p>
              <p class="block__text">{{ outboundFlightTotalDurationMinutes  }}</p>
            </span>
          </template>
        </div>
      </template>
    </div>
  </div>
  <!-- Terugvlucht -->
  <div class="block block--flight">
    <div class="block__content">
      <h3 class="block__title">Terugvlucht</h3>
      <p class="block__date">{{ date.format(new Date(flight.return_departure_date_time)) }}</p>
      <a @click="showFlightIn = !showFlightIn" class="block__more" :class="{ open: showFlightIn }">Vluchtinformatie</a>
    </div>
    <div class="block__flight">
      <span class="origin">
        <p class="block__time">{{ time.format(new Date(flight.return_departure_date_time)) }}</p>
        <p class="block__text">
          <FormatName :name="flight.destinationName" :origin="flight.destination" />
        </p>
      </span>
      <span class="duration">
        <p class="block__label">{{  returnFlightScheduleMessage }}</p>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 129.778 31.682">
          <path
            fill="#1A4C8D"
            d="m125.403 17.633.625.463a.834.834 0 0 1 .335.712l-.388 7.57 3.135 2.325a1.65 1.65 0 1 1-1.968 2.653l-3.135-2.325-7.132 2.566a.833.833 0 0 1-.778-.114l-.625-.464a.834.834 0 0 1 .084-1.393l5.229-2.985-3.224-2.392-1.766.415a.831.831 0 0 1-.687-.142l-1.028-.762 2.655-2.18 1.317-3.174 1.028.762a.834.834 0 0 1 .335.617l.115 1.81 3.223 2.392 1.341-5.87a.833.833 0 0 1 1.309-.484ZM1.471 23.149l1.357 1.47c-.484.447-.963.9-1.438 1.359L0 24.54c.486-.47.976-.933 1.471-1.39Zm3.03-2.672 1.289 1.53c-.503.423-1.002.853-1.497 1.29l-1.323-1.5a88.134 88.134 0 0 1 1.532-1.32Zm110.403-1.622c.528.418 1.051.844 1.57 1.275l-1.278 1.538a85.766 85.766 0 0 0-1.535-1.247l1.243-1.566ZM7.662 17.938l1.216 1.587c-.523.4-1.041.808-1.556 1.221l-1.252-1.56a90.956 90.956 0 0 1 1.592-1.248Zm104.008-1.53c.547.394 1.09.795 1.628 1.202l-1.207 1.595a85.306 85.306 0 0 0-1.59-1.174l1.169-1.623Zm-100.738-.857 1.142 1.642a84.18 84.18 0 0 0-1.61 1.146l-1.178-1.616a87.172 87.172 0 0 1 1.646-1.172Zm97.384-1.442c.565.368 1.126.743 1.682 1.125l-1.13 1.65a84.986 84.986 0 0 0-1.644-1.1l1.092-1.675Zm-93.994-.798 1.063 1.694c-.557.35-1.111.706-1.661 1.07l-1.102-1.669c.562-.372 1.13-.737 1.7-1.095Zm90.546-1.336c.582.341 1.16.69 1.733 1.045l-1.052 1.7a84.794 84.794 0 0 0-1.693-1.02l1.012-1.725Zm-87.083-.727.983 1.741c-.572.323-1.141.654-1.707.99l-1.023-1.718a86.752 86.752 0 0 1 1.747-1.013ZM101.317 10c.597.314 1.19.635 1.78.963l-.972 1.748a84.71 84.71 0 0 0-1.738-.94l.93-1.77Zm-79.955-.664.901 1.786c-.589.297-1.174.601-1.756.912l-.943-1.764a86.7 86.7 0 0 1 1.798-.934Zm76.32-1.14a86.71 86.71 0 0 1 1.824.88l-.889 1.79a84.71 84.71 0 0 0-1.781-.857l.847-1.812ZM25.03 7.593l.817 1.826a85.86 85.86 0 0 0-1.798.83L23.19 8.44c.61-.29 1.224-.573 1.84-.849Zm68.94-1.027a84.5 84.5 0 0 1 1.862.792l-.804 1.831a84.78 84.78 0 0 0-1.82-.774l.762-1.849Zm-65.196-.544.73 1.862c-.613.24-1.223.488-1.83.742l-.773-1.844c.621-.26 1.246-.514 1.873-.76Zm61.412-.912a86.9 86.9 0 0 1 1.896.704l-.718 1.867a84.9 84.9 0 0 0-1.852-.688l.674-1.883Zm-57.593-.483.642 1.894c-.624.211-1.245.43-1.863.656l-.686-1.879a86.953 86.953 0 0 1 1.907-.671Zm53.742-.795c.645.198 1.287.403 1.927.615l-.63 1.898a85.053 85.053 0 0 0-1.882-.6l.585-1.913Zm-49.922-.404.554 1.922a85.11 85.11 0 0 0-1.893.57l-.599-1.908a87.11 87.11 0 0 1 1.938-.584Zm46.013-.694c.653.167 1.304.342 1.953.524l-.54 1.926a85.221 85.221 0 0 0-1.91-.513l.497-1.937Zm-42.094-.339.465 1.946c-.64.153-1.28.313-1.916.481l-.51-1.934a87.28 87.28 0 0 1 1.961-.493ZM78.48 1.82c.66.137 1.318.282 1.975.434l-.45 1.948a85.388 85.388 0 0 0-1.931-.423l.406-1.959Zm-34.183-.275.374 1.965c-.647.123-1.293.254-1.938.392l-.42-1.955c.66-.142 1.322-.276 1.984-.402Zm30.197-.454c.667.106 1.332.22 1.996.341L76.13 3.4a85.541 85.541 0 0 0-1.95-.334l.314-1.975ZM48.292.88l.283 1.98c-.652.092-1.303.193-1.952.3l-.329-1.972c.665-.11 1.33-.214 1.998-.309ZM70.476.545c.67.075 1.34.158 2.007.249l-.268 1.982a85.669 85.669 0 0 0-1.962-.243l.223-1.988ZM52.328.4l.191 1.991a86.15 86.15 0 0 0-1.966.211L50.316.615c.67-.08 1.34-.152 2.012-.216ZM66.514.193A87.76 87.76 0 0 1 68.53.35l-.178 1.992a85.76 85.76 0 0 0-1.97-.154l.132-1.995ZM56.377.106l.098 1.997c-.658.033-1.317.073-1.974.12L54.356.23c.673-.049 1.347-.09 2.02-.123Zm6.101-.088c.674.013 1.347.035 2.02.064l-.086 1.998a85.813 85.813 0 0 0-1.975-.063l.041-2ZM60.436 0l.006 2c-.658.002-1.317.011-1.975.029l-.052-2A87.822 87.822 0 0 1 60.436 0Z"
          />
        </svg>
        <p class="block__label">{{ returnFlightTotalDurationMinutes }}</p>
      </span>
      <span class="destination">
        <p class="block__time">
          {{ time.format(new Date(flight.return_arrival_date_time)) }}
          <sup>{{ dayDifferenceReturn > 0 ? `+${dayDifferenceReturn}` : '' }}</sup>
        </p>
        <p class="block__text">
          <FormatName :name="flight.originName" :origin="flight.origin" />
        </p>
      </span>

    </div>
    <div class="block__footer">
      <template v-if="showFlightIn">
        <div class="block__content flex">
          <img :src="`data:image/png;base64,${KL}`" alt="klm" class="block__logo" />
        </div>
        <div class="block__meta">
          <template v-for="(schedule, index) in returnFlight" :key="index">
            <span>
              <p class="block__label">Vertrek</p>
              <p class="block__text">{{ schedule.originName }} ({{ schedule.airport_origin }})</p>
              <p class="block__text">{{ dateTime.format(new Date(schedule.departure_date_time)) }}</p>
            </span>
            <span>
              <p class="block__label">Aankomst</p>
              <p class="block__text">{{ schedule.destinationName }} ({{ schedule.airport_destination }})</p>
              <p class="block__text">{{ dateTime.format(new Date(schedule.arrival_date_time)) }}</p>
            </span>
            <span>
              <p class="block__label">Vluchtnummer</p>
              <p class="block__text">KLM - Delta ({{ schedule.flight_code }})</p>
            </span>
            <span>
              <p class="block__label">Tijdsduur</p>
              <p class="block__text">{{ returnFlightTotalDurationMinutes  }}</p>
            </span>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped></style>
